import React, {forwardRef, ReactNode, useImperativeHandle, useRef} from "react";
import Dialog from "@mui/material/Dialog";
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack, Theme,
  Typography
} from "@mui/material";
import _ from "lodash";
import {SxProps} from "@mui/system";

export interface FaModalDialogProps {
  title?: string | null,
  subtitleText?: string,
  maxWidth: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  maxHeight?: number,
  onAccept?: () => void,
  buttonTextAccept?: string,
  onOk?: () => void,
  buttonTextOk?: string,
  onReject?: () => void,
  buttonTextReject?: string,
  closeButtonVariant?: 'contained' | 'outlined' | 'text',
  children?: ReactNode,
  showClose?: boolean,
  isOpen?: boolean,
  noHeader?: boolean,
  footer?: ReactNode,
  footerText?: string,
  setIsOpen?: (open: boolean) => void;
  outsideClick?: () => void;
  sx?: SxProps<Theme>;
}

const FaModalDialog = forwardRef<any, FaModalDialogProps>((
  {
    title,
    subtitleText,
    maxWidth,
    maxHeight,
    onAccept,
    buttonTextAccept,
    onOk,
    buttonTextOk,
    onReject,
    buttonTextReject,
    closeButtonVariant = 'contained',
    showClose,
    children,
    isOpen,
    footer,
    footerText,
    setIsOpen,
    outsideClick,
    noHeader,
    sx
  }, ref) => {
  
  const fallbackTransl = {
    accept: 'Accept',
    ok: 'Ok',
    reject: 'Reject'
  };
  
  const [open, setOpen] = React.useState(false);
  const modalRef = useRef<any>();

  const handleClose = (reason?: any) => {
    if (reason === 'force' || !isOpen)
      setOpen(false);
    if (reason !== 'force' && !_.isEmpty(reason)) {
      if (outsideClick) {
        outsideClick();
      }
    }
  };

  useImperativeHandle(ref, () => ({
    show() {
      setOpen(true);
    },
    handleClose,
    modalRef: modalRef.current
  }));

  return (
    <Dialog
      ref={modalRef}
      onClose={handleClose}
      onBackdropClick={() => {
        if (setIsOpen) {
          setIsOpen(false);
          handleClose('force')
        }
      }}
      open={open}
      maxWidth={maxWidth}
      fullWidth
      sx={{
        ...sx,
        ...(maxHeight && {
          '& .MuiDialog-paper': {
            maxHeight: `${maxHeight}vh`
          }
        })
      }}
    >
      {
        !noHeader &&
        <>
          <DialogTitle sx={{pt: 2, pb: 1, px: 2, backgroundColor: ''}}>
            {/*@ts-ignore*/}
            <Stack direction={'row'} justifyContent={'space-between'} width={1} padding={0}>
              <Typography flexGrow={1} sx={{my: 'auto'}} variant={"h4"} color={"primary"}>{title}</Typography>
              {
                showClose &&
                <IconButton aria-label="close" size="small" color={'secondary'} onClick={handleClose}>
                  <CloseIcon/>
                </IconButton>
              }
            </Stack>
          </DialogTitle>
          {
            subtitleText &&
            <Typography sx={{px: 2, py: 1}}>{subtitleText}</Typography>
          }
          <Divider sx={{my: 0}}/>
        </>
      }
      <DialogContent className={'dialog-content'} sx={{padding: '20px 16px'}}>
        {children}
      </DialogContent>
      {
        <Stack direction={"column"} p={2}>
          {
            footer
          }
        </Stack>
      }
      <Divider sx={{my: 0}}/>
      {
        footerText &&
        <Typography sx={{px: 2, py: 2}}>{footerText}</Typography>
      }
      {
        (onOk || onAccept || onReject) &&
        <DialogActions>
          {
            onReject &&
            <Button variant={closeButtonVariant} onClick={() => {
              // handleClose('')
              if (onReject)
                onReject();
            }}>
              {buttonTextReject ?? fallbackTransl.reject}
            </Button>
          }
          {
            onOk &&
            <Button variant={"contained"} autoFocus onClick={() => {
              // handleClose('')
              if (onOk)
                onOk();
            }}>
              {buttonTextOk ?? fallbackTransl.ok}
            </Button>
          }
          {
            onAccept &&
            <Button variant={"contained"} onClick={() => {
              // handleClose('')
              if (onAccept)
                onAccept();
            }}>
              {buttonTextAccept ?? fallbackTransl.accept}
            </Button>
          }
        </DialogActions>
      }
    </Dialog>
  );
});
FaModalDialog.displayName = 'FactureeModalDialog';
export default FaModalDialog;